import type { ReactNode } from 'react';
import { Languages } from 'lucide-react';
import { useI18n } from '../lib/i18n';
import { VigLogo } from './Logo';

/** The card a signed-out page's form sits on, its heading, and the inputs inside it. */
export const AUTH_CARD = 'rounded-xl border border-ink-line bg-ink-card/90 p-6 shadow-edge-neutral backdrop-blur';
export const AUTH_TITLE = 'mb-5 font-display text-h2 text-neutral-50';
export const AUTH_INPUT = '!bg-ink-raised !border-ink-line !text-neutral-50';
export const AUTH_LINK = 'text-body-sm text-neutral-400 underline-offset-4 hover:text-neutral-50 hover:underline';

/** A field's label on the dark card. */
export function AuthLabel({ children }: { children: ReactNode }) {
  return <span className="text-neutral-300">{children}</span>;
}

/** What went wrong, on the dark card. */
export function AuthAlert({ children }: { children: ReactNode }) {
  return (
    <p role="alert" className="mt-4 rounded-md border border-error/30 bg-error/10 px-3 py-2 text-body-sm text-error-dark">
      {children}
    </p>
  );
}

/**
 * The frame of every signed-out page — sign in, forgot password, reset password:
 * the dark hero, the language switch, the logo above the card.
 */
export function AuthShell({ children }: { children: ReactNode }) {
  const { t, locale, setLocale } = useI18n();

  return (
    <div data-theme="dark" className="relative grid min-h-full place-items-center overflow-hidden bg-ink-deep px-4 py-10">
      {/* The dark hero treatment: mesh field over the machine grid. */}
      <div className="pointer-events-none absolute inset-0 bg-mesh animate-mesh-drift" aria-hidden />
      <div className="pointer-events-none absolute inset-0 bg-grid-dark bg-grid-size opacity-60" aria-hidden />

      <button
        type="button"
        onClick={() => setLocale(locale === 'en' ? 'ar' : 'en')}
        className="absolute end-4 top-4 z-10 inline-flex items-center gap-2 rounded-md border border-ink-line bg-ink-card/70 px-3 py-1.5 text-body-sm text-neutral-300 backdrop-blur hover:text-neutral-50"
      >
        <Languages className="h-4 w-4" aria-hidden />
        {locale === 'en' ? 'العربية' : 'English'}
      </button>

      <div className="relative w-full max-w-sm">
        <div className="mb-7 text-center">
          {/* The logo carries the brand name in print, so the heading text is
              screen-reader-only — and the logo itself stays hidden from
              assistive tech, or the name would be announced twice. */}
          <h1>
            <VigLogo className="mx-auto h-11 w-auto text-neutral-50" />
            <span className="sr-only">{t('app.name')}</span>
          </h1>
          <p className="mt-4 text-body-sm text-neutral-400">{t('auth.subtitle')}</p>
        </div>

        {children}

        <p className="mt-5 text-center text-caption text-[var(--text-tertiary)]">
          {t('app.name')} · {t('app.tagline')}
        </p>
      </div>
    </div>
  );
}
